import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { t } from "i18next";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../components/Loader";
import PreviewPicture from "../../../../components/PreviewPic/PreviewPicture";
import PropTypes from "prop-types";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CollectionsIcon from "@mui/icons-material/Collections";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import HistoryIcon from "@mui/icons-material/History";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import CancelIcon from "@mui/icons-material/Cancel";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import YardIcon from "@mui/icons-material/Yard";
import PrintIcon from "@mui/icons-material/Print";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ArrivedModal from "./ArrivedModal";
import PendingAgreementModal from "./PendingAgreementModal";
import CancelledOrReturnedModal from "./CancelledOrReturnedModal";
import ReactivateVehicleModal from "./ReactivateVehicleModal";

import { resetState } from "../../../../slices/vehicleInfoSlice";
import { resetState as addNewResetState } from "../../../../slices/addNewVehiclesSlice";
import { getCount } from "../../../../actions/getCountAction";
import MissingDataModal from "./MissingDataModal";
import VehiclePrintPreview from "./VehiclePrintPreview";

/* ---------- Columns ---------- */
const headCells = [
  { id: "expand", label: "", sortable: false },
  { id: "id", label: "ID", sortable: true },
  { id: "entryDate", label: "Entry Date", sortable: true },
  { id: "status", label: "Status", sortable: true },
  { id: "client", label: "Client Name", sortable: true },
  { id: "description", label: "Description", sortable: true },
  { id: "fuelType", label: "Fuel Type", sortable: true },
  { id: "destination", label: "Destination", sortable: true },
  { id: "vin", label: "VIN", sortable: true },
  { id: "bookingNumber", label: "Booking Number", sortable: false },
  { id: "warehouseArrivalDate", label: "Warehouse Arrival Date", sortable: true },
  { id: "etaWarehouseArrivalDate", label: "ETA Warehouse Arrival Date", sortable: true },
  { id: "picture", label: "Picture", sortable: false },
  { id: "actions", label: "Actions", sortable: false },
];

/* ---------- Helpers ---------- */
const createData = (e) => {
  const make = e.make?.name || "";
  const model = e.model?.name || "";
  const description = `${e.vehicleType?.name} ${e.year ?? ""} ${make} ${model} ${e.color?.name || ""}`.trim();
  return {
    id: e.id,
    entryDate: e.entryDate,
    status: e.status,
    client: e.client?.name,
    vehicleType: e.vehicleType?.name,
    description,
    fuelType: e.fuelType?.name,
    vin: e.vin,
    transportCompany: e.transportCompany?.name,
    etaWarehouseArrivalDate: e.etaWarehouseArrivalDate,
    picture: e.warehousePictures || [],
    color: e.color,
    arrivalNote: e.arrivalNote,
    warehouseArrivalDate: e.warehouseArrivalDate,
    key: e.key,
    title: e.title,
    hasCatalyst: e.hasCatalyst,
    partsWithCar: e.partsWithCar,
    removeEngine: e.removeEngine,
    departureDate: e.departureDate,
    cancellationNote: e.cancellationNote,
    destination: e.destination,
    price: e.price,
    weight: e.weight,
    purchasePriceFile: e.purchasePriceFile || [],
    container: e.container,
    booking: e.bookingDTO,
    dealer:e.client?.dealer || false,
    object:e
  };
};

const isRowMissingData = (row, isClient) => {
  const isArrived = row?.status?.id === 2;
  const isCancelledOrReturned = row?.status?.id === 3 || row?.status?.id === 4;


  if (isClient) return false;

  if (isCancelledOrReturned) return false;

  const missing = [
    row.price === 0,
    !row.transportCompany,
    !row.destination,
   // row.purchasePriceFile?.length === 0,
    isArrived && (!row.weight || row.weight === 0),
    isArrived && (row.picture?.length === 0),
  ];


  return missing.some(Boolean);
};

const MissingDataBadge = ({ row, withCheckbox, handleOpenMissingDataModal ,selectedVehicle , handleSelectVehicle,isClient }) => {
  const isMissingData = isRowMissingData(row,isClient);



  const isChecked = selectedVehicle?.some((v) => v.id === row.id);


  
  if (!isMissingData && !withCheckbox ) return null;
  if ((!isMissingData || row.dealer) && withCheckbox) {
    return <Checkbox checked={isChecked} onChange={(e) => handleSelectVehicle(e, row.object)} />;
  }

  if (!isMissingData) return null;

  const missingItems = [
    row.price === 0 ? t("Price is missing") : null,
    row?.status?.id === 2 && (!row.weight || row.weight === 0) ? t("Weight is missing") : null,
   // row.purchasePriceFile?.length === 0 ? t("Purchase Price File is missing") : null,
    row?.status?.id === 2 && row.picture?.length === 0 ? t("Picture is missing") : null,
    !row.transportCompany ? t("Transport Company is missing") : null,
    !row.destination ? t("Destination is missing") : null,
  ].filter(Boolean);

 

  return (
    <Tooltip
      title={
        <Box sx={{ minWidth: 240, maxWidth: 320 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberRoundedIcon sx={{ color: 'error.main' }} fontSize="small" />
            <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 700 }}>
              {t("Missing data")}
            </Typography>
          </Box>

          <Box sx={{ mb: 1 }}>
            {missingItems.map((text, i) => (
              <Typography
                key={i}
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: 'error.main',
                    borderRadius: '50%',
                    display: 'inline-block',
                  }}
                />
                {text}
              </Typography>
            ))}
          </Box>

          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={()=>handleOpenMissingDataModal(row)}
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(244,67,54,0.25)',
              '&:hover': { boxShadow: '0 6px 16px rgba(244,67,54,0.35)' },
            }}
          >
            {t("Fix Missing Data")}
          </Button>
          
        </Box>
      }
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={100}
      componentsProps={{
        tooltip: {
          sx: {
            p: 1.5,
            bgcolor: '#fff',
            color: 'text.primary',
            border: '1px solid #f1f1f1',
            boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
          },
        },
      }}
      disableFocusListener
      disableTouchListener
      interactive
    >
      <IconButton
        aria-label="Missing Data"
        title="Missing Data"
        color="error"
        size="small"
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 28,
          height: 28,
          p: 0.5,
          ml: 0.5,
          mr: 0.5,
          bgcolor: "#fff",
          border: "1.5px solid #f44336",
          boxShadow: "0 1px 4px 0 rgba(244,67,54,0.10)",
          animation: "shake 0.7s cubic-bezier(.36,.07,.19,.97) both",
          "@keyframes shake": {
            "10%, 90%": { transform: "translateX(-1px)" },
            "20%, 80%": { transform: "translateX(2px)" },
            "30%, 50%, 70%": { transform: "translateX(-4px)" },
            "40%, 60%": { transform: "translateX(4px)" },
          },
          "&:hover": { bgcolor: "#fdecea", borderColor: "#d32f2f" },
        }}
      >
        <PriorityHighIcon fontSize="small" />
      </IconButton>
     
    </Tooltip>
  );
};

MissingDataBadge.defaultProps = { handleOpenMissingDataModal: undefined };

/* ---------- Sub components ---------- */
const RowActions = ({
  row,
  isClient,
  isSuperUser,
  isAdminUser,
  isWarehouseStaff,
  isShippingStaff,
  isAbleDelete,
  onEdit,
  onArrived,
  onPending,
  onCancelOrReturn,
  onStatusChange,
  onViewShipments,
  onViewContainer,
  onViewLogs,
  onDelete,
  onPreview,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title={t("Actions")}>
        <IconButton aria-label="actions" onClick={handleOpen} size="small">
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {((!isClient&& (isSuperUser || isAdminUser)) || row.status?.id === 1) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onEdit();
            }}
          >
            <ModeEditIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Edit")}
          </MenuItem>
        )}

        {row.status?.id === 1 && (!isClient &&(isSuperUser || isAdminUser || isShippingStaff ||isWarehouseStaff )) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onArrived();
            }}
          >
            <WhereToVoteIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Arrived Dialog")}
          </MenuItem>
        )}

        {row.status?.id === 2 && (!isClient && (isSuperUser || isAdminUser)) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onPending();
            }}
          >
            <PauseCircleFilledIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Pending Agreement")}
          </MenuItem>
        )}

        {(row.status?.id === 1 || row.status?.id === 3) && (!isClient && (isSuperUser || isAdminUser)) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onCancelOrReturn();
            }}
          >
            <CancelIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Cancelled or Returned")}
          </MenuItem>
        )}

        {row.status?.id === 4 && (!isClient && (isSuperUser || isAdminUser)) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onStatusChange();
            }}
          >
            <RestartAltIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Reactivate to Arrived")}
          </MenuItem>
        )}

        {row.booking?.id && (!isClient && (isSuperUser || isShippingStaff || isAdminUser)) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onViewShipments();
            }}
          >
            <DirectionsBoatIcon fontSize="small" style={{ marginRight: 8 }} /> {t("View Shipment")}
          </MenuItem>
        )}


        {row.booking?.id && (isClient || isSuperUser || isShippingStaff || isAdminUser) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onViewContainer();
            }}
          >
            <YardIcon fontSize="small" style={{ marginRight: 8 }} /> {t("View Container")}
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleClose();
            onPreview && onPreview();
          }}
        >
          <PrintIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Preview View")}
        </MenuItem>

        {!isClient && (isSuperUser ) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onViewLogs();
            }}
          >
            <HistoryIcon fontSize="small" style={{ marginRight: 8 }} /> {t("History Logs")}
          </MenuItem>
        )}

        {isAbleDelete && !isClient && (isSuperUser ) && (
          <MenuItem
            onClick={() => {
              handleClose();
              onDelete();
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteForeverIcon fontSize="small" style={{ marginRight: 8 }} /> {t("Delete")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const CollapseView = ({ row }) => {
  const InfoRow = ({ label, value, bold }) => (
    <Typography
      variant={bold ? "button" : "body1"}
      sx={{
        textAlign: bold ? "center" : "start",
        borderBottomStyle: "solid",
        borderWidth: "thin",
        width: "100%",
        color: "secondary.main",
        minHeight: 27,
      }}
    >
      {value ?? "***"}
    </Typography>
  );

  const yesNo = (flag) =>
    flag ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        gap: 2,
        justifyContent: "start",
        m: 2,
        backgroundColor: "colorA.main",
      }}
    >
      {/* Dates & Color */}
      <Box sx={{ display: "flex", width: "35%", m: 2 }}>
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <InfoRow bold value="INFO" />
          <InfoRow label value="SEAL NUMBER" />
          <InfoRow label value="VESSEL NAME" />
          <InfoRow label value="VOYAGE NUMBER" />
          <InfoRow label value="ETA SAILING DATE" />
          <InfoRow label value="ETA ARRIVAL DATE" />
        </Box>
        <Box sx={{ width: "1px", backgroundColor: "secondary.main", mx: 1 }} />
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "start", gap: 1 }}>
          <InfoRow bold value="DETAILS" />
          <InfoRow value={row.booking?.sealNumber || 'N/A'} />
          <InfoRow value={row.booking?.vesselName || 'N/A'} />
          <InfoRow value={row.booking?.voyageNumber || 'N/A'} />
          <InfoRow value={row.booking?.estimatedSailingDate || 'N/A'} />
          <InfoRow value={row.booking?.estimatedArrivalDate || 'N/A'} />
        </Box>
      </Box>

      <Box sx={{ width: "1px", backgroundColor: "secondary.main", mt: 4, mb: 4 }} />
      <Box sx={{ width: "1px", backgroundColor: "secondary.main", mt: 4, mb: 4 }} />

      {/* Flags */}
      <Box sx={{ display: "flex", width: "35%", m: 2 }}>
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <InfoRow bold value="INFO" />
          <InfoRow label value="KEY" />
          <InfoRow label value="TITLE" />
          <InfoRow label value="REMOVE ENGINE" />
          <InfoRow label value="HAS CATALYST" />
          <InfoRow label value="PARTS WITH VEHICLE" />
        </Box>
        <Box sx={{ width: "1px", backgroundColor: "secondary.main", mx: 1 }} />
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "start", gap: 1 }}>
          <InfoRow bold value="DETAILS" />
          <InfoRow value={yesNo(row.key)} />
          <InfoRow value={yesNo(row.title)} />
          <InfoRow value={yesNo(row.removeEngine)} />
          <InfoRow value={yesNo(row.hasCatalyst)} />
          <InfoRow value={yesNo(row.partsWithCar)} />
        </Box>
      </Box>

      <Box sx={{ width: "1px", backgroundColor: "secondary.main", mt: 4, mb: 4 }} />
      <Box sx={{ width: "1px", backgroundColor: "secondary.main", mt: 4, mb: 4 }} />

      {/* Other */}
      <Box sx={{ display: "flex", width: "35%", m: 2 }}>
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <InfoRow bold value="INFO" />
          <InfoRow label value="WAREHOUSE ARRIVAL NOTE" />
          <InfoRow label value="SHIPPING LINE" />
          <InfoRow label value="PORT LOADING" />
          <InfoRow label value="PICKUP TERMINAL" />
          <InfoRow label value="RETURN TERMINAL" />

        </Box>
        <Box sx={{ width: "1px", backgroundColor: "secondary.main", mx: 1 }} />
        <Box sx={{ display: "flex", width: "47%", flexDirection: "column", alignItems: "start", gap: 1 }}>
          <InfoRow bold value="DETAILS" />
          <InfoRow value={row?.arrivalNote || 'N/A'} />
          <InfoRow value={row?.booking?.shippingLineName || 'N/A'} />
          <InfoRow value={row?.booking?.portLoading || 'N/A'} />
          <InfoRow value={row?.booking?.pickupTerminal || 'N/A'} />
          <InfoRow value={row?.booking?.returnTerminal || 'N/A'} />
        </Box>
      </Box>
    </Box>
  );
};

CollapseView.propTypes = { row: PropTypes.object.isRequired };

function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

/* ---------- Head ---------- */
function EnhancedTableHead({ order, orderBy, onRequestSort, withCheckbox }) {
  const createSortHandler = (property) => (event) => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {headCells.map((hc) => {
          if (withCheckbox && hc.id === "actions") return null;
          const content = hc.sortable ? (
            <TableSortLabel
              active={orderBy === hc.id}
              direction={orderBy === hc.id ? order : "asc"}
              onClick={createSortHandler(hc.id)}
            >
              {t(hc.label).toUpperCase()}
              {orderBy === hc.id && (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              )}
            </TableSortLabel>
          ) : (
            t(hc.label)
          );

          return (
            <TableCell
              key={hc.id}
              padding="normal"
              sortDirection={hc.sortable && orderBy === hc.id ? order : false}
              variant="head"
              sx={{ whiteSpace: "nowrap" }}
            >
              {content}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  isSelectedTable: PropTypes.bool,
};

/* ---------- Main ---------- */
const VehiclesTable = ({ handlingFetchData, isClient, selectedVehicle , handleSelectVehicle , withCheckbox = false , listVehiclesNotShowing = [],isSuperUser,isShippingStaff,isWarehouseStaff , isAdminUser }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchData = useSelector((state) => state.fetchData);
  const userReducer = useSelector((state) => state.user);

  const [dense, setDense] = useState(true);
  const [openPreviewPicture, setOpenPreviewPicture] = useState(false);
  const [pictureFile, setPictureFile] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [openArrivalModal, setOpenArrivalModal] = useState(false);
  const [openPendingAgreementModal, setOpenPendingAgreementModal] = useState(false);
  const [openCancelledOrReturnedModal, setOpenCancelledOrReturnedModal] = useState(false);
  const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isAbleDelete, setAbleDelete] = useState(false);
  const [openMissingDataModal,setOpenMissingDataModal] = useState(false);
  const [previewVehicle, setPreviewVehicle] = useState(null);


  // Query params
  const page = Number(searchParams.get("PageNumber")) || 0;
  const rowsPerPage = Number(searchParams.get("PageSize")) || 5;
  const order = searchParams.get("SortOrder") || "desc";
  const orderBy = searchParams.get("SortBy") || "id";

  const setPage = (val) => {
    searchParams.set("PageNumber", val);
    setSearchParams(searchParams);
  };
  const setRowsPerPage = (val) => {
    searchParams.set("PageSize", val);
    setSearchParams(searchParams);
  };
  const setOrder = (val) => {
    searchParams.set("SortOrder", val);
    setSearchParams(searchParams);
  };
  const setOrderBy = (val) => {
    searchParams.set("SortBy", val);
    setSearchParams(searchParams);
  };

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleClosePreview = () => {
    setOpenPreviewPicture(false);
    setPictureFile([]);
  };
  const handleOpenPreview = (file) => {
    setPictureFile(file);
    setOpenPreviewPicture(true);
  };

  const handleEdit = (id) => {
    dispatch(resetState());
    navigate(`/VehicleaddEdit?vehicleId=${id}&backLink=` + searchParams.toString().replaceAll("&", "*"));
  };
  const handleViewShipments = (id) => navigate(`/shipments?BookingId=${id}`);

  const handleViewContainer = (id) => navigate(`/containers?BookingId=${id}`);


  const handleViewLogs = (id) => navigate(`/log?VehiclesId=${id}`);

  const toggleOpen = (rowId) =>
    setOpenSet((prev) => {
      const s = new Set(prev);
      s.has(rowId) ? s.delete(rowId) : s.add(rowId);
      return s;
    });
  const isOpen = (rowId) => openSet.has(rowId);

  const handleOpenDeleteModal = (item) => {
    setSelectedDeleteItem(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleOpenArrivalModal = (id) => {
    setSelectedVehicleId(id);
    setOpenArrivalModal(true);
  };
  const handleCloseArrivalModal = () => {
    setOpenArrivalModal(false);
    setSelectedVehicleId(null);
    handlingFetchData();
    dispatch(addNewResetState());
    dispatch(getCount({ mode: "vehicle" }));
  };

  const handleOpenPendingAgreementModal = (id) => {
    setSelectedVehicleId(id);
    setOpenPendingAgreementModal(true);
  };
  const handleClosePendingAgreementModal = () => {
    setOpenPendingAgreementModal(false);
    setSelectedVehicleId(null);
    handlingFetchData();
    dispatch(addNewResetState());
    dispatch(getCount({ mode: "vehicle" }));
  };

  const handleOpenCancelledOrReturnedModal = (id) => {
    setSelectedVehicleId(id);
    setOpenCancelledOrReturnedModal(true);
  };
  const handleCloseCancelledOrReturnedModal = () => {
    setOpenCancelledOrReturnedModal(false);
    setSelectedVehicleId(null);
    handlingFetchData();
    dispatch(addNewResetState());
    dispatch(getCount({ mode: "vehicle" }));
  };

  const handleOpenStatusChangeModal = (id) => {
    setSelectedVehicleId(id);
    setOpenStatusChangeModal(true);
  };
  const handleCloseStatusChangeModal = () => {
    setOpenStatusChangeModal(false);
    setSelectedVehicleId(null);
    handlingFetchData();
    dispatch(addNewResetState());
    dispatch(getCount({ mode: "vehicle" }));
  };

  const handelCloseMissingDataModal = async () => {

    await setOpenMissingDataModal(false);
           handlingFetchData();

  }
  const handleOpenMissingDataModal = (row) => {
    setSelectedVehicleId(row.id);
    setOpenMissingDataModal(true);
  };

  useEffect(() => {
    const roles = userReducer?.userInfo?.user?.roles || [];
    setAbleDelete(roles.some((r) => ["ROLE_SUPER_EDITOR", "ROLE_SUPER", "ROLE_ADMIN"].includes(r.name)));
  }, [userReducer]);

  const data = fetchData.Object || {};
  const content = data.content || [];
  const filterVehicles = content.filter((vehicle) => !listVehiclesNotShowing.some((v) => v.id === vehicle.id));
  const rows = useMemo(() => filterVehicles.map(createData), [filterVehicles]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (data.totalElements || 0)) : 0;

  if (data.loading === "pending" || data.loading === "idle" || userReducer.loading === true) {
    return <Loader />;
  }

  const colSpan = headCells.length - (withCheckbox ? 1 : 0);

  return (
    <Box sx={{ width: "100%", bgcolor: "#F7F8FA", p: { xs: 0, md: 2 }, borderRadius: 3 }}>
      <Paper sx={{ width: "100%", mb: 2, borderRadius: 3, boxShadow: "0 2px 12px 0 rgba(11, 60, 93, 0.10)" }}>
        <TableContainer sx={{ borderRadius: 3 }}>
          <Table sx={{ minWidth: 750, background: "#fff", borderRadius: 3 }} aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              withCheckbox={withCheckbox}
            />
            <TableBody>
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    sx={{
                      backgroundColor: row.id % 2 === 0 ? "#F7F8FA" : "#fff",
                      "&:hover": { backgroundColor: "#E9ECEF" },
                      transition: "background 0.2s",
                    }}
                    tabIndex={-1}
                  >
                    {/* Expand */}
                    <TableCell>
                      <IconButton aria-label="expand row" size="small" onClick={() => toggleOpen(row.id)}>
                        {isOpen(row.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>

                    {/* ID + Missing badge */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {row.id}
                        <MissingDataBadge
                          row={row}
                          withCheckbox={withCheckbox}
                          handleOpenMissingDataModal={handleOpenMissingDataModal}
                          selectedVehicle={selectedVehicle}
                          isClient={isClient}
                          handleSelectVehicle={handleSelectVehicle}
                        />
                      </Box>
                    </TableCell>

                    <TableCell>{row.entryDate}</TableCell>
                    <TableCell>{row.status?.name}</TableCell>
                    <TableCell>{row.client}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.fuelType}</TableCell>
                    <TableCell>{row.destination?.name || 'N/A'}</TableCell>
                    <TableCell>{row.vin}</TableCell>
                    <TableCell>{row.booking?.bookingNumber || 'N/A'}</TableCell>
                    <TableCell>{row?.warehouseArrivalDate || 'N/A'}</TableCell>
                    <TableCell>{row?.etaWarehouseArrivalDate || 'N/A'}</TableCell>

                    {/* Picture */}
                    <TableCell>
                      {row.picture.length !== 0 ? (
                        <IconButton onClick={() => handleOpenPreview(row.picture)} aria-label="view" title="view">
                          <CollectionsIcon />
                        </IconButton>
                      ) : (
                        <CloseIcon />
                      )}
                    </TableCell>

                    {/* Actions */}
                    {!withCheckbox && (
                      <TableCell>
                        <RowActions
                          row={row}
                          isClient={isClient}
                          isAbleDelete={isAbleDelete}
                          onEdit={() => handleEdit(row.id)}
                          onArrived={() => handleOpenArrivalModal(row.id)}
                          onPending={() => handleOpenPendingAgreementModal(row.id)}
                          onCancelOrReturn={() => handleOpenCancelledOrReturnedModal(row.id)}
                          onStatusChange={() => handleOpenStatusChangeModal(row.id)}
                          onViewShipments={() => handleViewShipments(row.booking.id)}
                          onViewContainer={() => handleViewContainer(row.booking.id)}
                          onViewLogs={() => handleViewLogs(row.id)}
                          onDelete={() => handleOpenDeleteModal(row)}
                          isSuperUser={isSuperUser}
                          isAdminUser={isAdminUser}
                          isShippingStaff={isShippingStaff}
                          isWarehouseStaff={isWarehouseStaff}
                          onPreview={() => setPreviewVehicle(row.object)}
                        />
                      </TableCell>
                    )}
                  </TableRow>

                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={colSpan}>
                      <Collapse in={isOpen(row.id)} timeout="auto" unmountOnExit>
                        <CollapseView row={row} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}

              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={colSpan} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.totalElements || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          ActionsComponent={TablePaginationActions}
          sx={{ bgcolor: "#fff", borderRadius: 2, boxShadow: "0 1px 4px 0 rgba(11, 60, 93, 0.08)", mt: 1 }}
        />
      </Paper>

      <FormControlLabel
        control={<Switch checked={dense} onChange={(e) => setDense(e.target.checked)} />}
        label={t("Densepadding")}
        sx={{ mt: 1, ml: 1 }}
      />

      {openPreviewPicture && (
        <PreviewPicture open={openPreviewPicture} handleClose={handleClosePreview} label={"PreviewPicture"} file={pictureFile} />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal isOpen={isDeleteModalOpen} onClose={handleCloseDeleteModal} deleteItem={selectedDeleteItem} />
      )}

      {openArrivalModal && (
        <ArrivedModal isOpen={openArrivalModal} onClose={handleCloseArrivalModal} vehicleId={selectedVehicleId} />
      )}

      {openPendingAgreementModal && (
        <PendingAgreementModal
          isOpen={openPendingAgreementModal}
          onClose={handleClosePendingAgreementModal}
          vehicleId={selectedVehicleId}
        />
      )}

      {openCancelledOrReturnedModal && (
        <CancelledOrReturnedModal
          isOpen={openCancelledOrReturnedModal}
          onClose={handleCloseCancelledOrReturnedModal}
          vehicleId={selectedVehicleId}
        />
      )}

      {openStatusChangeModal && (
        <ReactivateVehicleModal
          isOpen={openStatusChangeModal}
          onClose={handleCloseStatusChangeModal}
          vehicleId={selectedVehicleId}
        />
      )}

       {openMissingDataModal && (
        <MissingDataModal
          isOpen={openMissingDataModal}
          onClose={()=> handelCloseMissingDataModal()}
          vehicleId={selectedVehicleId}
        />
      )}

        {previewVehicle && (
          <VehiclePrintPreview
            open={!!previewVehicle}
            onClose={() => setPreviewVehicle(null)}
            vehicle={previewVehicle}
          />
        )}
    </Box>
  );
};

VehiclesTable.displayName = "VehiclesTable";
VehiclesTable.propTypes = {
  handlingFetchData: PropTypes.func.isRequired,
  isClient: PropTypes.bool.isRequired,
  isSuperUser: PropTypes.bool,

  isShippingStaff: PropTypes.bool,
  isWarehouseStaff: PropTypes.bool,
  isAdminUser: PropTypes.bool,
  selectedVehicleParm: PropTypes.object,
};

export default VehiclesTable;
